// src/guidance/journey/run-status.ts
//
// What a journey run's statuses MEAN. `test_runs.status` is the coarse projection of the outcome
// (run-outcome.ts statusForOutcome): its 'failed' mostly means `bug`, a run that did its job. Judge a run
// by `test_runs.outcome_status` through this module, never by comparing the coarse status to 'failed'.
// Field semantics: docs/backend/test-runs.md, "Outcome semantics".

import type { JourneyRunOutcome, OutcomeStatus } from './run-outcome.ts'

export const OUTCOME_STATUS_LABELS = {
  passed: 'Passed',
  bug: 'App bug found',
  failed: 'Failed: session mismatch',
  incomplete: 'Incomplete',
  blocked: 'Blocked',
  infra: 'Infrastructure failure',
  'could-not-verify': 'Could not verify',
  continued: 'Continuation outcome',
  unknown: 'Unknown canonical outcome',
} as const satisfies Record<OutcomeStatus | 'unknown', string>

export type CanonicalOutcomeStatus = keyof typeof OUTCOME_STATUS_LABELS

/** @public Read by scripts/dev. Every stored `test_runs.outcome_status`; `unknown` is read-side only. */
export const OUTCOME_STATUSES = Object.keys(OUTCOME_STATUS_LABELS)
  .filter((status): status is OutcomeStatus => status !== 'unknown')

/** @public Read by scripts/dev. Missing or unrecognized outcomes are `unknown`, never inferred from the coarse status. */
export function canonicalOutcomeStatus(outcomeStatus: string | null | undefined): CanonicalOutcomeStatus {
  return outcomeStatus && Object.hasOwn(OUTCOME_STATUS_LABELS, outcomeStatus)
    ? outcomeStatus as CanonicalOutcomeStatus
    : 'unknown'
}

// The run ended without a verdict on the app: the journey itself failed. `bug` is a verdict.
export const FAILED_OUTCOME_STATUSES = ['failed', 'incomplete', 'blocked', 'infra', 'could-not-verify'] as const satisfies readonly OutcomeStatus[]

export function isFailedOutcome(outcome: JourneyRunOutcome): outcome is Extract<JourneyRunOutcome, { status: typeof FAILED_OUTCOME_STATUSES[number] }> {
  return (FAILED_OUTCOME_STATUSES as readonly string[]).includes(outcome.status)
}

// Cancelled runs were stopped or aged out of the queue, so their outcome says nothing about the journey:
// a FAILED journey run has a FAILED outcome and this is not its status.
const CANCELLED_RUN_STATUS = 'cancelled'

/** @public Read by scripts/dev. The statusForOutcome image of FAILED outcomes, minus {@link CANCELLED_RUN_STATUS}. */
export const FAILED_RUN_STATUSES = ['failed', 'incomplete', 'blocked', 'infra-failed'] as const

/** @public Read by scripts/dev. A settled, not cancelled run with a FAILED outcome: finalization writes the status with the outcome. */
export function isFailedRun(run: { status: string | null; outcome_status: string | null }): boolean {
  return (FAILED_RUN_STATUSES as readonly (string | null)[]).includes(run.status)
    && (FAILED_OUTCOME_STATUSES as readonly (string | null)[]).includes(run.outcome_status)
}

/** Not finished yet. `queued` exists only in read projections of a journey's latest run. */
export function isPendingJourneyRun(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'in-progress'
}

export function isCancelledRun(status: string | null | undefined): boolean {
  return status === CANCELLED_RUN_STATUS
}

/**
 * Whether the run's `outcome_*` columns are final. Finalization writes them with the status
 * (run-outcome.ts statusForOutcome), whose only 'cancelled' is `infra:never-ran`. A pending run holds a
 * provisional report; a run cancelled outside finalization holds a void one.
 */
export function isSettledRunOutcome(run: { status: string | null | undefined; outcome_reason: string | null | undefined }): boolean {
  return !isPendingJourneyRun(run.status) && (!isCancelledRun(run.status) || run.outcome_reason === 'never-ran')
}

// 'incomplete' and 'blocked' ended without a verdict; their terminal transition files the reason into task_errors.
const ATTENTION_RUN_STATUSES = new Set(['failed', 'infra-failed', 'incomplete', 'blocked'])

/** Finished short of a clean pass. Takes a {@link displayRunStatus} value: `bug-found` is a verdict, not attention. Not {@link isFailedOutcome}. */
export function isAttentionRunStatus(status: string | null | undefined): boolean {
  return !!status && ATTENTION_RUN_STATUSES.has(status)
}

export const BUG_FOUND_RUN_STATUS = 'bug-found'

/**
 * How a surface shows a run to a human: a finished `bug` run is a verdict, not a fault. Never persisted
 * and never a control predicate. A redacted `outcome_status` is null, so a hidden security bug stays 'failed'.
 * Its gate is a settled `bug` ({@link isSettledRunOutcome}), except legacy eval `completed` + `bug` rows
 * (docs/backend/test-runs.md, "Settlement").
 */
export function displayRunStatus<S extends string | null>(status: S, outcomeStatus: string | null | undefined): S | typeof BUG_FOUND_RUN_STATUS {
  return status === 'failed' && outcomeStatus === 'bug' ? BUG_FOUND_RUN_STATUS : status
}

/** Run counts keyed by {@link displayRunStatus}. */
export function countByDisplayStatus(rows: readonly { status: string | null; outcome_status: string | null; c: number }[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const r of rows) {
    const status = displayRunStatus(r.status, r.outcome_status)
    if (status) counts.set(status, (counts.get(status) ?? 0) + r.c)
  }
  return counts
}
